import React, { useState, useRef, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { Link, router } from '@inertiajs/react'
import { LogOut, ChevronDown, Building2, LifeBuoy } from 'lucide-react'
import { cn } from '@/lib/utils'
import { initialsFromEmail, displayNameFromEmail } from '@/lib/identity'

interface UserMenuProps {
  currentUser: { id: number; email: string }
  // Kept for caller compatibility only. "Billing & plan" was replaced by the
  // always-visible "Business settings" item (/account) — the settings pages adapt
  // to the user's role themselves, so nothing in this menu is billing-gated anymore.
  canManageBilling?: boolean
  // Target for "Help & guides". Guides are global (/guides), so both headers pass the
  // same destination. The item ALWAYS renders — the two menus are intentionally
  // identical. Defaulted to the guides index so it never renders dead.
  helpHref?: string
  /**
   * Where "Business settings" goes — `currentAccount.settingsPath`, served by the backend. The
   * business is in the path now, so this menu cannot name a settings URL without being told which
   * business it is in. Null (a page whose currentAccount prop has not landed) hides the item
   * rather than guessing a URL.
   */
  settingsHref?: string | null
}

// Reusable account/avatar menu shared by the per-property TopBar and the portfolio
// PortfolioLayout header. Renders an avatar button that opens a portal dropdown with
// the user's identity, contextual actions, and the destructive Log out.
//
// The two headers are intentionally IDENTICAL: every menu surfaces the same items —
// Business settings, Help & guides, Log out.
export default function UserMenu({
  currentUser,
  canManageBilling: _canManageBilling = false,
  helpHref = '/guides',
  settingsHref = null,
}: UserMenuProps) {
  const [open, setOpen] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState({ top: 0, right: 0 })

  const initials = initialsFromEmail(currentUser.email)
  const displayName = displayNameFromEmail(currentUser.email)

  const updatePos = useCallback(() => {
    if (!buttonRef.current) return
    const rect = buttonRef.current.getBoundingClientRect()
    setPos({ top: rect.bottom + 6, right: window.innerWidth - rect.right })
  }, [])

  useEffect(() => {
    if (!open) return
    updatePos()
    const handler = (e: MouseEvent) => {
      const target = e.target as Node
      if (buttonRef.current?.contains(target) || panelRef.current?.contains(target)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    window.addEventListener('scroll', updatePos, true)
    window.addEventListener('resize', updatePos)
    return () => {
      document.removeEventListener('mousedown', handler)
      window.removeEventListener('scroll', updatePos, true)
      window.removeEventListener('resize', updatePos)
    }
  }, [open, updatePos])

  // Destructive action — the documented exception to the <Link> navigation rule.
  // Clears only the :user warden scope (sign_out_all_scopes = false), never an AdminUser /admin session.
  const signOut = () => {
    setOpen(false)
    router.delete('/users/sign_out')
  }

  const itemClass =
    'flex w-full items-center gap-2.5 px-3.5 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-muted'

  return (
    <>
      <button
        ref={buttonRef}
        onClick={() => setOpen(!open)}
        aria-label="Account menu"
        aria-expanded={open}
        className="flex items-center gap-2.5 rounded-lg py-1 pl-1 pr-1.5 transition-colors hover:bg-muted"
      >
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
          {initials}
        </span>
        <span className="hidden text-sm font-medium text-foreground sm:inline-block">{displayName}</span>
        <ChevronDown
          className={cn('hidden h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform duration-200 sm:block', open && 'rotate-180')}
        />
      </button>

      {open &&
        createPortal(
          <div
            ref={panelRef}
            className="fixed z-[9999] w-64 overflow-hidden rounded-xl border border-border/60 bg-white shadow-xl shadow-foreground/10 animate-slide-in"
            style={{ top: pos.top, right: pos.right }}
          >
            <div className="flex items-center gap-3 border-b border-border/40 px-3.5 py-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
                {initials}
              </span>
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold text-foreground">{displayName}</div>
                <div className="truncate text-xs text-muted-foreground">{currentUser.email}</div>
              </div>
            </div>

            <div className="py-1">
              {/* Present whenever the business is known — the settings pages adapt to the user's role. */}
              {settingsHref && (
                <Link href={settingsHref} onClick={() => setOpen(false)} className={itemClass}>
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  Business settings
                </Link>
              )}
              {/* Always present — both headers expose the same Help item. */}
              <Link href={helpHref} onClick={() => setOpen(false)} className={itemClass}>
                <LifeBuoy className="h-4 w-4 text-muted-foreground" />
                Help &amp; guides
              </Link>
            </div>

            <div className="border-t border-border/40 py-1">
              <button
                onClick={signOut}
                className="flex w-full items-center gap-2.5 px-3.5 py-2.5 text-sm font-medium text-rose-600 transition-colors hover:bg-rose-50"
              >
                <LogOut className="h-4 w-4" />
                Log out
              </button>
            </div>
          </div>,
          document.body,
        )}
    </>
  )
}
